import { action, computed, flow, makeObservable, observable } from 'mobx';
import { Canceler } from 'axios';
import { IPagination, LoadingStatus, paginationCast, TPagination } from '~/types/main';
import { defaultPagination as commonDefaultPagination } from '~/constants/main';
import {
  sortFieldsMap,
  CRM_RESTRICTIONS_COLUMNS,
  ICustomerRestrictionsFilters,
  ICustomerRestrictionsSorting,
  IRestriction,
  TCrmRestrictionsItem,
  TCustomerRestrictionValidation,
  ISearchCustomer,
} from '~/view/screens/CrmScreen/screens/CrmRestrictionsScreen/types';
import logger from '~/services/logger';
import client from '~/services/client';
import {
  defaultCustomersRestrictionsFilters,
  defaultPagination,
  defaultRestriction,
  paymentTypes,
} from '~/view/screens/CrmScreen/screens/CrmRestrictionsScreen/constants';
import { toastSuccess } from '~/utils/toasts';
import { localeStore } from '~/mobx/localesStore';
import { defaultValidation, SORTING_DIRECTION } from '~/constants/main';
import { crmRestrictionsDTO } from '~/view/screens/CrmScreen/screens/CrmRestrictionsScreen/dto';

export class CrmRestrictionsStore {
  constructor() {
    makeObservable(this);
  }

  @observable crmRestrictionsList: TCrmRestrictionsItem[] = [];
  @observable loadingStatus = LoadingStatus.Init;
  @observable restriction: IRestriction = defaultRestriction;

  private crmRestrictionsGettingTimeoutId: NodeJS.Timeout | undefined = undefined;
  private crmRestrictionsGetAbortController = new AbortController();
  private crmRestrictionsCustomerLookupAbortController = new AbortController();

  @observable filters: ICustomerRestrictionsFilters = defaultCustomersRestrictionsFilters;
  @observable sorting: ICustomerRestrictionsSorting = {
    sortField: CRM_RESTRICTIONS_COLUMNS.DISABLED_AT,
    sortDir: SORTING_DIRECTION.DESCENDING,
  };
  @observable pagination: TPagination = defaultPagination;
  @observable validationErrors: TCustomerRestrictionValidation = { customer: defaultValidation, payment_type: defaultValidation };

  cancel: Canceler | null = null;

  @observable isEdit: boolean = false;
  @observable loading: boolean = false;

  @computed get isNoCrmRestrictionsFound() {
    return [LoadingStatus.Success, LoadingStatus.Error].includes(this.loadingStatus) && this.crmRestrictionsList.length === 0;
  }

  @action changeSort = ({ sortField, sortDir }: Partial<ICustomerRestrictionsSorting>) => {
    if (sortField && sortDir) {
      this.sorting = { sortField, sortDir };
      this._getCrmRestrictionsWithDelay();
    }
  };

  @action public getCrmRestrictions = (page?: number) => {
    if (this.loadingStatus === LoadingStatus.Init) {
      this._getCrmRestrictionsWithNoDelay(1);
    } else {
      this._getCrmRestrictionsWithNoDelay(page || 1);
    }
  };

  @action private _getCrmRestrictionsWithDelay = (pageNumber: number = 1) => {
    clearTimeout(this.crmRestrictionsGettingTimeoutId);

    this.crmRestrictionsGettingTimeoutId = setTimeout(() => {
      this._getCrmRestrictions(pageNumber);
    }, 500);
  };

  @action private _getCrmRestrictionsWithNoDelay = (pageNumber: number = 1) => {
    clearTimeout(this.crmRestrictionsGettingTimeoutId);
    this._getCrmRestrictions(pageNumber);
  };

  @action changeFilters = ({ search }: Partial<ICustomerRestrictionsFilters>) => {
    if (typeof search === 'string') {
      this.filters.search = search;
      this._getCrmRestrictionsWithDelay();
    }
  };

  @action public resetSearch = () => {
    this.filters.search = '';
    this._getCrmRestrictionsWithNoDelay();
  };

  @action clearEditing = () => {
    this.restriction = defaultRestriction;
    this.filters = defaultCustomersRestrictionsFilters;
    this.validationErrors = { customer: defaultValidation, payment_type: defaultValidation };
  };

  @action private _getCrmRestrictions = flow(function* (this: CrmRestrictionsStore, page: number): any {
    this.crmRestrictionsGetAbortController.abort();
    this.crmRestrictionsGetAbortController = new AbortController();

    try {
      const params = this._makeFetchCrmRestrictionsParams(page);
      this.loadingStatus = LoadingStatus.Loading;

      const { data = [], ...pagination } = yield client.get('/v3/crm/customers/restrictions', {
        params,
        signal: this.crmRestrictionsGetAbortController.signal,
      });

      this.pagination = paginationCast(pagination);
      this.crmRestrictionsList = crmRestrictionsDTO(data);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error) {
      if (this.crmRestrictionsGetAbortController.signal.aborted) {
        this.loadingStatus = LoadingStatus.Aborted;
      } else {
        this.loadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'crmRestrictionsStore _getCrmRestrictions' });
      }
    }
  }).bind(this);

  @action private _makeFetchCrmRestrictionsParams = (page: number) => {
    const params: { [key: string]: string | number } = {
      page,
      search: this.filters.search,
    };

    if (this.sorting.sortDir && this.sorting.sortField) {
      params.sort_field = sortFieldsMap[this.sorting.sortField];
      params.sort_dir = this.sorting.sortDir;
    }

    return params;
  };

  @action validate = () => {
    this.validateCustomer();
    this.validatePaymentType();
  };

  @action validateCustomer = () => {
    if (!this.restriction.customer.length) {
      this.validationErrors.customer = { errorText: localeStore.t('required_field_error'), isValid: false };
    } else {
      this.validationErrors.customer.isValid = true;
    }
  };

  @action validatePaymentType = () => {
    if (!Object.keys(paymentTypes).map(Number).includes(this.restriction.payment_type)) {
      this.validationErrors.payment_type = { errorText: localeStore.t('required_field_error'), isValid: false };
    } else {
      this.validationErrors.payment_type.isValid = true;
    }
  };

  @action public changeRestrictionHandler = (value: Partial<IRestriction>) => {
    if (!this.restriction) return;
    this.restriction = { ...this.restriction, ...value };
  };

  @action public createRestriction = async () => {
    if (!this.restriction) return;

    try {
      this.loadingStatus = LoadingStatus.Loading;

      const data = {
        user_id: this.restriction.customer[0].value,
        payment_types: [this.restriction.payment_type],
        comment: this.restriction.comment,
      };

      await client.post('/v3/crm/customers/restrictions', data);

      this.loadingStatus = LoadingStatus.Success;
      toastSuccess({ message: localeStore.t('toast_crm_restriction_saved') });
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;

      logger.error({ error, place: 'crmRestrictionsStore createRestriction', showModal: true });
    }
  };

  @action public fetchCustomers = async (page: number, searchValue: string = ''): Promise<IPagination<ISearchCustomer>> => {
    this.crmRestrictionsCustomerLookupAbortController.abort();
    this.crmRestrictionsCustomerLookupAbortController = new AbortController();
    try {
      const pagination: IPagination<ISearchCustomer> = await client.get('/v3/crm/customers/lookup', {
        params: {
          page,
          phone: searchValue.toLowerCase(),
          is_delete: 0,
        },
        signal: this.crmRestrictionsCustomerLookupAbortController.signal,
      });
      return pagination;
    } catch (error: any) {
      if (!this.crmRestrictionsGetAbortController.signal.aborted) {
        logger.error({ error, place: 'crmRestrictionsStore fetchCustomers' });
      }
      return commonDefaultPagination;
    }
  };

  async deleteRestriction(restrictionId: number) {
    try {
      await client.delete(`/v3/crm/customers/restrictions/${restrictionId}`);
      this._getCrmRestrictionsWithNoDelay();
    } catch (error) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'crmRestrictionsStore deleteRestriction' });
    }
  }
}

export const crmRestrictionsStore = new CrmRestrictionsStore();
